import { isServer } from '@tunein/web-utils';
import { Client, fetchExchange, subscriptionExchange, } from '@urql/core';
import asyncRetry from 'async-retry';
import { createClient as createWSClient, } from 'graphql-ws';
import { getSdk } from '../../graphQLSDK.generated';
import { pipe, subscribe } from 'wonka';
import WebSocket from 'ws';
class SubscriptionError extends Error {
    errorData;
    data;
    constructor(errorData, data) {
        super('Subscription error');
        this.errorData = errorData;
        this.data = data;
    }
}
export class GraphQLClient {
    #sdk;
    #client;
    #graphQlDomain;
    #customFetch;
    constructor(graphQlDomain, customFetch) {
        this.#graphQlDomain = graphQlDomain;
        this.#customFetch = customFetch;
        this.#sdk = getSdk(this.#sdkWrapper);
        this.#client = this.#initClient();
    }
    get sdk() {
        return this.#sdk;
    }
    #fetchOverride = (url, options) => {
        return this.#customFetch({
            url,
            skipUrlTransform: true,
            parseAs: null,
            skipAllResponseTransforms: true,
            ...options,
        });
    };
    #sdkWrapper = async (doc, vars, subscriptionCallback) => {
        const { selectionSet, operation } = doc.definitions[0];
        const resultDataKey = selectionSet.selections[0].name.value;
        if (operation === 'subscription') {
            return this.#subscription(doc, vars, subscriptionCallback, resultDataKey);
        }
        const data = await asyncRetry(async () => {
            const result = await this.#client?.[operation](doc, vars).toPromise();
            if (result.error) {
                throw result.error;
            }
            return result.data;
        }, { retries: 2 });
        return data[resultDataKey] || data;
    };
    #subscription(document, variables, callback, resultDataKey) {
        const client = this.#initClient(true);
        const subscriptionStream = client.subscription(document, variables);
        const onSubscriptionData = (result) => {
            if (!result.data && !result.error) {
                return;
            }
            const data = result.data?.[resultDataKey] || result.data;
            if (!result.error) {
                callback(data);
                return;
            }
            const { message, graphQLErrors, networkError } = result.error || {};
            const errorData = {
                message,
                networkError: networkError?.message,
                graphQLErrors: graphQLErrors?.map((error) => {
                    const { name = 'Unknown', message, originalError, } = error?.toJSON() || {};
                    return {
                        message: `[${name}] ${message}`,
                        originalErrorMessage: originalError?.message,
                    };
                }),
            };
            callback(new SubscriptionError(errorData, data));
        };
        const { unsubscribe } = pipe(subscriptionStream, subscribe(onSubscriptionData));
        return unsubscribe;
    }
    #createForwardSubscriptionConfig(wsClient) {
        return {
            forwardSubscription(request) {
                return {
                    subscribe(sink) {
                        const unsubscribe = wsClient.subscribe(request, sink);
                        return { unsubscribe };
                    },
                };
            },
        };
    }
    #initUrqlClient = (wsClient) => {
        const clientExchanges = [fetchExchange];
        if (wsClient) {
            clientExchanges.push(subscriptionExchange(this.#createForwardSubscriptionConfig(wsClient)));
        }
        return new Client({
            url: `${this.#graphQlDomain}/query`,
            exchanges: clientExchanges,
            fetch: this.#fetchOverride,
            requestPolicy: 'network-only',
        });
    };
    #initWsClient = () => {
        return createWSClient({
            url: `${this.#graphQlDomain.replace('https', 'wss')}/query`,
            keepAlive: 30_000,
            ...(isServer() && { webSocketImpl: WebSocket }),
        });
    };
    #initClient = (useSubscriptions = false) => {
        const wsClient = useSubscriptions ? this.#initWsClient() : null;
        return this.#initUrqlClient(wsClient);
    };
}
